import { getApiBase } from "./apiBase";
import { track } from "../utils/analytics";

const getToken = () => localStorage.getItem("token");

const request = async (path, options = {}) => {
  const token = getToken();
  const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${getApiBase()}${path}`, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    // Stale/invalid JWT — let the app force-logout instead of letting callers
    // misinterpret the failure (e.g. KidSetup loop when /kids/ 401s on boot).
    if (res.status === 401 && token) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }
    const error = new Error(err.detail || "Request failed");
    error.status = res.status;
    throw error;
  }
  return res.json();
};

// ── Auth ────────────────────────────────────────────────────────────────────
export const authApi = {
  googleLogin: async (name, email, photo, idToken, role = "parent", ref = undefined) => {
    const url = ref ? `/auth/google?ref=${encodeURIComponent(ref)}` : "/auth/google";
    const result = await request(url, {
      method: "POST",
      body: JSON.stringify({ name, email, photo, id_token: idToken || null, role }),
    });
    // sign_up only when the backend just created the account; it used to fire on every login.
    track(result.is_new ? "sign_up" : "login", { method: "Google", role: result.role });
    return result;
  },
  me: () => request("/auth/me"),
  getReferralCode: () => request("/auth/referral-code"),
};

// ── Kids ────────────────────────────────────────────────────────────────────
export const kidsApi = {
  list: () => request("/kids/"),
  create: (data) => request("/kids/", { method: "POST", body: JSON.stringify(data) }),
  update: (id, data) => request(`/kids/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (id) => request(`/kids/${id}`, { method: "DELETE" }),
  assignments: (id) => request(`/kids/${id}/assignments`),
};

// ── Sessions ─────────────────────────────────────────────────────────────────
export const sessionsApi = {
  save: (kidId, data) =>
    request(`/sessions/${kidId}`, { method: "POST", body: JSON.stringify(data) }),
  list: (kidId) => request(`/sessions/${kidId}`),
  stats: (kidId) => request(`/sessions/${kidId}/stats`),
  mastery: (kidId, subject) =>
    request(`/sessions/${kidId}/mastery${subject ? "?subject=" + encodeURIComponent(subject) : ""}`),
  timeline: (kidId, days = 90) => request(`/sessions/${kidId}/timeline?days=${days}`),
};

// ── Words ─────────────────────────────────────────────────────────────────────
export const wordsApi = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/words/${q ? "?" + q : ""}`);
  },
  letters: () => request("/words/letters"),
  categories: (lang) => request(`/words/categories${lang ? "?lang=" + lang : ""}`),
};

// ── AI Questions ──────────────────────────────────────────────────────────────
export const aiApi = {
  generate: (data) =>
    request("/ai/questions", { method: "POST", body: JSON.stringify(data) }),
};

// ── Journey ───────────────────────────────────────────────────────────────────
export const journeyApi = {
  getProgress: (kidId) => request(`/journey/${kidId}`),
  completeNode: (kidId, data) =>
    request(`/journey/${kidId}/complete`, { method: "POST", body: JSON.stringify(data) }),
};

// ── Audio (admin) ─────────────────────────────────────────────────────────────
export const audioApi = {
  manifest: () => request("/audio/manifest"),
  records:  () => request("/audio/records"),

  upload: async (key, blob, filename = "recording.webm", meta = {}) => {
    const token = getToken();
    const params = new URLSearchParams({
      type:     meta.type     || "word",
      label:    meta.label    || key,
      language: meta.language || "he",
    });
    const form = new FormData();
    form.append("file", blob, filename);
    const res = await fetch(
      `${getApiBase()}/audio/upload/${encodeURIComponent(key)}?${params}`,
      {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || "Upload failed");
    }
    return res.json();
  },

  delete: (key) =>
    request(`/audio/${encodeURIComponent(key)}`, { method: "DELETE" }),
};

// ── Analytics ─────────────────────────────────────────────────────────────────
export const analyticsApi = {
  logShare: (kidId, gameId, type) => {
    track("share", { content_type: type });
    return request("/analytics/share", {
      method: "POST",
      body: JSON.stringify({ kid_id: kidId, game_id: gameId, type }),
    }).catch(() => {});  // fire-and-forget, swallow errors
  },
};

// ── Classrooms ────────────────────────────────────────────────────────────────
export const classroomsApi = {
  create: (name) =>
    request("/classrooms/", { method: "POST", body: JSON.stringify({ name }) }),
  list: () => request("/classrooms/"),
  join: (kidId, code) =>
    request("/classrooms/join", { method: "POST", body: JSON.stringify({ kid_id: kidId, code }) }),
  getStudents: (classroomId) => request(`/classrooms/${classroomId}/students`),
  getStudentMastery: (classroomId, kidId, subject) =>
    request(`/classrooms/${classroomId}/students/${kidId}/mastery${subject ? "?subject=" + encodeURIComponent(subject) : ""}`),
  removeMember: (classroomId, kidId) =>
    request(`/classrooms/${classroomId}/members/${kidId}`, { method: "DELETE" }),
  analytics: (classroomId) => request(`/classrooms/${classroomId}/analytics`),
  rename: (classroomId, name) =>
    request(`/classrooms/${classroomId}`, { method: "PATCH", body: JSON.stringify({ name }) }),
  listAssignments: (classroomId) => request(`/classrooms/${classroomId}/assignments`),
  createAssignment: (classroomId, data) =>
    request(`/classrooms/${classroomId}/assignments`, { method: "POST", body: JSON.stringify(data) }),
  deleteAssignment: (classroomId, assignmentId) =>
    request(`/classrooms/${classroomId}/assignments/${assignmentId}`, { method: "DELETE" }),
};
